import React, { useEffect, useState } from 'react';
import { Database, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { accountsApi } from '../../services/api/endpoints/accounts';
import type { InitializationStatus } from '../../types/account-initialization';

interface FiscalYearInitStatusBadgeProps {
  fiscalYearId: string;
  /** Affichage compact (juste l'icône) ou détaillé */
  compact?: boolean;
  /** Callback quand le statut est chargé */
  onStatusLoaded?: (status: InitializationStatus) => void;
}

/**
 * Badge affichant le statut d'initialisation des comptes pour un exercice fiscal
 */
export function FiscalYearInitStatusBadge({ 
  fiscalYearId, 
  compact = false,
  onStatusLoaded 
}: FiscalYearInitStatusBadgeProps) {
  const [status, setStatus] = useState<InitializationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadStatus = async () => {
      try {
        setLoading(true);
        setError(false);
        const response = await accountsApi.getInitializationStatus(fiscalYearId);
        
        if (response.success && response.data) {
          const statusData = (response.data as any).data || response.data;
          setStatus(statusData);
          onStatusLoaded?.(statusData);
        } else {
          // Non initialisé
          const defaultStatus: InitializationStatus = {
            isInitialized: false,
            accountCount: 0,
            framework: null,
            fiscalYearId
          };
          setStatus(defaultStatus);
          onStatusLoaded?.(defaultStatus);
        }
      } catch (err) {
        console.error('Error loading init status:', err);
        setError(true);
        // Assumer non initialisé en cas d'erreur
        const defaultStatus: InitializationStatus = {
          isInitialized: false,
          accountCount: 0,
          framework: null,
          fiscalYearId
        };
        setStatus(defaultStatus);
      } finally {
        setLoading(false);
      }
    };

    loadStatus();
  }, [fiscalYearId, onStatusLoaded]);

  if (loading) {
    return (
      <span className="inline-flex items-center gap-1 text-muted-foreground">
        <Loader2 className="w-3 h-3 animate-spin" />
        {!compact && <span className="text-xs">Vérification...</span>}
      </span>
    );
  }

  if (error) {
    return null; // Ne pas afficher en cas d'erreur
  }

  if (status?.isInitialized) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
        <CheckCircle2 className="w-3 h-3" />
        {!compact && (
          <>
            <span>{status.accountCount} comptes</span>
            <span className="text-green-600 dark:text-green-500">({status.framework})</span>
          </>
        )}
        {compact && <span>Init.</span>}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
      <AlertCircle className="w-3 h-3" />
      {!compact && <span>Non initialisé</span>}
      {compact && <span>Non init.</span>}
    </span>
  );
}
